import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { ScheduleDetailResponse } from '../types/gantt.types'
import { scheduleListDetailKey } from './useScheduleListData'

// 리스트 보기 비고 (구현계획 C-04) — 초안·접힘(기본 접힘)·저장.
//  저장 = PUT /schedule/schedules/{id} { remark }, 500자. [비고 저장]은 내용이 있을 때만 활성(canSave).
//  표시는 GanttRemarkBar 를 재사용하므로 반환 이름을 그 props 와 맞췄다(remarkHidden·remarkDraft·setRemarkHidden·setRemarkDraft·
//  scheduleRemark·remarkSaving·handleRemarkSave) — 값 전달만 하면 된다.

/** 비고 최대 글자 수 */
export const REMARK_MAX_LENGTH = 500

export interface UseScheduleListRemarkArgs {
  scheduleId: number | null | undefined
  /** 저장된 비고(공종 상세 응답의 remark). 조회 전이면 undefined */
  savedRemark: string | undefined
}

export function useScheduleListRemark({ scheduleId, savedRemark }: UseScheduleListRemarkArgs) {
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  const sid = scheduleId ?? null
  const saved = savedRemark ?? ''

  const [remarkDraft, setRemarkDraft] = useState(saved)
  const [remarkHidden, setRemarkHidden] = useState(true)
  const [remarkSaving, setRemarkSaving] = useState(false)

  // 현장이 바뀌면 접힘으로 되돌리고, 저장된 값이 바뀌면(조회 완료·저장 반영) 초안을 그 값으로 맞춘다 — 렌더 중 상태 조정
  const [prevSid, setPrevSid] = useState(sid)
  const [prevSaved, setPrevSaved] = useState(saved)
  if (prevSid !== sid) {
    setPrevSid(sid)
    setPrevSaved(saved)
    setRemarkDraft(saved)
    setRemarkHidden(true)
    setRemarkSaving(false)
  } else if (prevSaved !== saved) {
    setPrevSaved(saved)
    setRemarkDraft(saved)
  }

  /** [비고 저장] 활성 조건 — 내용이 있고 500자 이하이며 저장 중이 아님 */
  const canSave = sid != null && remarkDraft.trim().length > 0 && remarkDraft.length <= REMARK_MAX_LENGTH && !remarkSaving

  const handleRemarkSave = useCallback(async () => {
    if (sid == null || !canSave) return
    const draft = remarkDraft
    setRemarkSaving(true)
    const headers = {
      ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    try {
      await apiClient.put(`/schedule/schedules/${sid}`, { remark: draft }, { headers })
      // 공종 상세 캐시의 비고만 교체 — 전체 재조회 없이 저장 값을 반영
      qc.setQueryData<ScheduleDetailResponse>(scheduleListDetailKey(user?.compId, sid), prev => (prev ? { ...prev, remark: draft } : prev))
    } catch {
      toast.error('저장에 실패했습니다.')
    } finally {
      setRemarkSaving(false)
    }
  }, [sid, canSave, remarkDraft, user?.compId, user?.memberId, qc])

  return {
    remarkHidden, setRemarkHidden,
    remarkDraft, setRemarkDraft,
    scheduleRemark: saved,
    remarkSaving, handleRemarkSave,
    canSave,
    maxLength: REMARK_MAX_LENGTH,
  }
}

export type ScheduleListRemark = ReturnType<typeof useScheduleListRemark>
